import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Typography,
  Stack,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Route as RouteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const DeviceTable = ({
  devices,
  totalDevicesInOrg,
  page,
  rowsPerPage,
  loadingDevices,
  onPageChange,
  onRowsPerPageChange,
  onDeviceAction
}) => {
  if (loadingDevices) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading devices...</Typography>
      </Box>
    );
  }

  if (devices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
        No devices found for this organization.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 'bold' } }}>
              <TableCell>Device UUID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Network</TableCell>
              <TableCell>Vehicle ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registered At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id} hover>
                <TableCell>
                  <Tooltip title={device.device_uuid}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {device.device_uuid.substring(0, 8)}...
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {device.device_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {device.network_name ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <RouteIcon fontSize="inherit" sx={{ color: 'text.secondary' }}/>
                      <Typography variant="caption">
                        {device.network_name} ({device.network_code})
                      </Typography>
                    </Stack>
                  ) : (
                    <Chip label="Organization-Wide" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {device.vehicle_id || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={device.is_active ? 'Active' : 'Registered'} 
                    size="small"
                    color={device.is_active ? 'success' : 'info'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {device.registered_at ? new Date(device.registered_at).toLocaleDateString() : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => onDeviceAction('View', device)}
                        sx={{ color: 'primary.main' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Device">
                      <IconButton 
                        size="small" 
                        onClick={() => onDeviceAction('Edit', device)}
                        sx={{ color: 'warning.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Device">
                      <IconButton 
                        size="small" 
                        onClick={() => onDeviceAction('Delete', device)} 
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalDevicesInOrg}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </>
  );
};

export default DeviceTable; 